import { createFolderSchema, type FolderKind } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input, SelectMenu, SelectMenuItem } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { flattenFolders, useCreateFolder, useFolderTree, useUpdateFolder } from './api';

/** Sentinel for the "none" option: a select value is always a string. */
const NONE = 'none';

interface FolderValues {
  name: string;
  parentId?: string | null;
}

export interface FolderFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  /** Which of the project's two trees this folder belongs to. */
  kind: FolderKind;
  /** Set when editing an existing folder. */
  folder?: { id: string; name: string; parentId: string | null };
  /** Pre-selected parent when creating a subfolder. */
  defaultParentId?: string | null;
}

export const FolderFormDialog = ({
  open,
  onOpenChange,
  projectCode,
  kind,
  folder,
  defaultParentId,
}: FolderFormDialogProps) => {
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const isEdit = folder !== undefined;
  const { data: tree } = useFolderTree(projectCode, kind);
  const create = useCreateFolder(projectCode, kind);
  const update = useUpdateFolder(projectCode, kind);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FolderValues>({
    resolver: zodResolver(createFolderSchema.omit({ kind: true })),
    defaultValues: { name: '', parentId: null },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      name: folder?.name ?? '',
      parentId: folder?.parentId ?? defaultParentId ?? null,
    });
  }, [open, folder, defaultParentId, reset]);

  // A folder cannot become its own parent.
  const options = flattenFolders(tree ?? []).filter((option) => option.id !== folder?.id);

  const onSubmit = handleSubmit(async (values) => {
    const parentId = values.parentId ?? null;
    try {
      if (isEdit) {
        await update.mutateAsync({ folderId: folder.id, input: { name: values.name, parentId } });
        toast.success(t('folders.updated'));
      } else {
        await create.mutateAsync({ name: values.name, parentId });
        toast.success(t('folders.created'));
      }
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['name', 'parentId'])) {
        toast.error(errorText(error, 'folders.saveFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('folders.editTitle') : t('folders.new')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t('folders.create')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.name')} error={errors.name?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input id={id} invalid={invalid} aria-describedby={describedBy} autoFocus {...register('name')} />
          )}
        </Field>

        <Field label={t('folders.parent')} error={errors.parentId?.message}>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="parentId"
              render={({ field }) => (
                <SelectMenu
                  id={id}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  value={field.value ?? NONE}
                  onValueChange={(value) => field.onChange(value === NONE ? null : value)}
                >
                  <SelectMenuItem value={NONE}>{t('folders.noParent')}</SelectMenuItem>
                  {options.map((option) => (
                    <SelectMenuItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              )}
            />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
